/**
 * PolicyTemplateViewer — renders a built-in policy template with
 * digital signature canvas, typed-name option, and signed-PDF download.
 * Used on AcknowledgePage when templateKey is present.
 */
import { useRef, useState, useEffect } from "react";
import jsPDF from "jspdf";

const SHAKEN_BABY = {
  title: "How to Identify & Prevent Shaken Baby Syndrome, Abusive Head Trauma & Child Maltreatment",
  sections: [
    {
      heading: "Background",
      text: "Shaken Baby Syndrome (SBS) and Abusive Head Trauma (AHT) occur when an infant or young child is violently shaken or subjected to impact forces to the head. This can cause severe brain injury, permanent disability, or death. All staff are required to understand and follow this policy."
    },
    {
      heading: "Recognizing Signs of Abusive Head Trauma",
      items: [
        "Unexplained irritability or excessive crying",
        "Difficulty staying awake or unusual drowsiness",
        "Loss of appetite or difficulty feeding",
        "Vomiting without apparent cause",
        "Seizures or convulsions",
        "Pale, blue, or blotchy skin"
      ]
    },
    {
      heading: "Responding to Suspected Abuse",
      items: [
        "Remove the child from the situation immediately and ensure their safety",
        "Do NOT shake, hold upside down, or roughly handle the child in any way",
        "Call 911 immediately if the child appears to be in danger or shows physical symptoms"
      ]
    },
    {
      heading: "Reporting",
      text: "All staff are mandated reporters. Any suspected child maltreatment must be reported immediately to the Director AND to your state's child abuse hotline."
    },
    {
      heading: "Prevention Strategies — If a Child is Distressed",
      items: [
        "Check for basic needs: hunger, diaper change, discomfort or illness",
        "Use soothing techniques: rocking gently, singing, quiet environment, change of scenery",
        "If feeling overwhelmed, place the child safely in their crib and step away briefly — ask a colleague for relief"
      ]
    },
    {
      heading: "Additional Prevention Techniques",
      items: [
        "Never leave an infant or toddler alone with an untrained individual",
        "Conduct regular staff training on appropriate infant handling and stress management",
        "Post visual reminders of 'Never Shake a Baby' in all infant and toddler rooms",
        "Maintain appropriate child-to-staff ratios at all times",
        "Encourage open communication among staff when feeling overwhelmed",
        "Report any concerning behavior by staff or visitors to the Director immediately",
        "Review this policy annually with all staff and document training"
      ]
    },
    {
      heading: "In Addition, the Facility Will",
      items: [
        "Provide regular support and mental health resources for staff to manage work-related stress",
        "Maintain a culture of zero tolerance for any form of physical abuse or inappropriate handling of children"
      ]
    },
    {
      heading: "Prohibited Behaviors — Staff May NEVER",
      items: [
        "Shake, toss, throw, or otherwise subject any child to violent or rough movement",
        "Strike, slap, pinch, or physically discipline any child in any way",
        "Use frightening, demeaning, or threatening language or actions toward any child"
      ]
    }
  ],
  acknowledgementText: "I acknowledge that I have read, understand, and agree to comply with this policy. I understand that violations may result in disciplinary action, termination, and/or legal action."
};

const EMERGENCY_PROCEDURES = {
  title: "Emergency Procedures — Staff Training Review",
  intro: "This review summarizes key emergency procedures all staff must understand and follow. Refer to the full Emergency Plan & Procedure for detailed instructions.",
  sections: [
    {
      heading: "Evacuation",
      items: [
        "Director or designee calls 911 and immediately alerts all staff",
        "Line children at the nearest clear exit — conduct head count",
        "Walk calmly to the designated assembly area — yield for emergency vehicles",
        "Do not re-enter the building until 'All Clear' is given",
        "OCDEL/DECAL notified within 24 hours"
      ]
    },
    {
      heading: "Lockdown",
      items: [
        "Lock and secure all outside doors and windows immediately",
        "Close curtains or blinds — turn off lights",
        "Keep everyone away from doors and windows — stay on the floor out of sight",
        "Keep children calm by reading or talking quietly",
        "Call 911 from a safe location — do not open doors until police give all clear",
        "Notify parents of all lockdowns (practice or real)"
      ]
    },
    {
      heading: "Fire Procedure",
      items: [
        "Sound the fire alarm — line children at the nearest exit door",
        "Conduct head count — escort children in single file to the fire assembly area",
        "Call 911 after evacuation — contact parents",
        "Do not re-enter until cleared by emergency personnel",
        "OCDEL/DECAL notified within 24 hours"
      ]
    },
    {
      heading: "Medical Emergencies",
      items: [
        "Call 911 immediately for serious injuries or unresponsive children",
        "Do not move the child — provide first aid if trained (kits located in every room)",
        "Contact child's parents — child transported to designated hospital",
        "OCDEL/DECAL notified within 24 hours"
      ]
    },
    {
      heading: "Loss of Utilities (Water, Electricity, Heat, Cooling)",
      items: [
        "Contact the appropriate utility company immediately",
        "Notify parents via phone, email, or text",
        "Close if temperature drops to 65°F or below (heat) or rises to 85°F or above (cooling)",
        "OCDEL/DECAL notified if program is closed for extended time"
      ]
    },
    {
      heading: "Missing Child",
      items: [
        "Teacher notifies Director immediately — conduct head count",
        "Note description of child's clothing immediately",
        "Search playground, vehicles, bathrooms, and closets",
        "If not found: call 911, notify parents",
        "OCDEL/DECAL notified within 24 hours"
      ]
    },
    {
      heading: "Dangerous Person",
      items: [
        "Alert all staff immediately — initiate LOCKDOWN procedures",
        "Call 911 from a safe location",
        "Do not physically restrain or confront — isolate from children if person is inside",
        "If children are outside and unsafe: gather and evacuate to designated site"
      ]
    },
    {
      heading: "Bomb Threat",
      items: [
        "Signal another staff member to call 911 — do not hang up on caller",
        "Note exact words, caller's voice, background sounds, and caller ID",
        "Do NOT touch suspicious packages — avoid using cell phones or 2-way radios",
        "Confer with police regarding evacuation — follow EVACUATION procedure if directed"
      ]
    }
  ],
  acknowledgementText: "I acknowledge that I have reviewed and understand the emergency procedures outlined above and agree to follow them as required. I understand it is my responsibility to refer to the full Emergency Plan & Procedure document for detailed instructions."
};

const DISCLOSURE_STATEMENT = {
  title: "DISCLOSURE STATEMENT",
  intro: "APPLICATION FOR CHILD CARE EMPLOYMENT, INCLUDING PROVISIONAL EMPLOYMENT\nRequired by your State's Child Protective Service Law [State Law Reference]",
  sections: [
    {
      heading: "Clearance Affirmation — Provisional Employment",
      text: "I swear/affirm that, if being hired on a provisional basis, I have applied for the State Child Abuse History Clearance, the State Police Criminal History Clearance, the Federal Bureau of Investigations (FBI) Criminal History Clearance, the National Sex Offender Registry (NSOR) verification, and any clearances for any state other than this State, in which I currently reside or have resided within the past five (5) years and have provided a copy of the appropriate completed request forms to the employer, administrator, supervisor or other person responsible for employment decisions and have submitted the results of the State Child Abuse History Clearance, the NSOR verification, any clearances for any state other than this State, which I currently reside or have resided within the past five (5) years, AND either the State Police Criminal History Clearance OR the FBI Criminal History Clearance to the employer, administrator, supervisor or other person responsible for employment decisions."
    },
    {
      heading: "Provisional Employment Limitations",
      text: "I understand that I may be hired on a provisional basis for a single period not to exceed a total of 45 days but am not permitted to work alone with children and must work in the immediate vicinity of a permanent employee during the provisional period."
    },
    {
      heading: "Child Abuse Registry",
      text: "I swear/affirm that I have not been named in the Statewide database as a perpetrator of an indicated or founded report of child abuse."
    },
    {
      heading: "Criminal History — Disqualifying Offenses",
      text: "I swear/affirm that I have not been convicted of any of the following offenses, the attempt, solicitation or conspiracy to commit any of the offenses, or an offense similar in nature, of the state consolidated statutes (relating to crimes and offenses), under the laws or former laws of the United States or one of its territories or possessions, another state, the District of Columbia, the Commonwealth of Puerto Rico or a foreign nation, or under a former law of this State."
    },
    {
      heading: "Chapter 25 — Listed Offenses",
      items: [
        "Criminal homicide",
        "Aggravated assault",
        "Stalking",
        "Strangulation",
        "Kidnapping",
        "Unlawful restraint",
        "Rape",
        "Statutory sexual assault",
        "Involuntary deviate sexual intercourse",
        "Sexual assault",
        "Aggravated indecent assault",
        "Indecent assault",
        "Indecent exposure",
        "Arson and related offenses",
        "Incest",
        "Concealing death of child",
        "Endangering welfare of children",
        "Dealing in infant children",
        "Prostitution and related offenses",
        "Obscene and other sexual material and performances",
        "Corruption of minors",
        "18 U.S.C § 2261 (relating to sexual abuse of children)",
        "18 U.S.C. § 2262 (relating to interstate domestic violence)",
        "Interstate violation of protection order"
      ]
    },
    {
      heading: "Controlled Substance Offenses",
      text: "I swear/affirm that I have not been convicted of a felony offense under the controlled substance, drug, device and cosmetic act committed within the past five (5) years."
    },
    {
      heading: "Sex Offender Registry",
      items: [
        "My name does not appear on a Statewide database or its equivalent as a perpetrator of child abuse.",
        "My name does not appear on the National Crime Information Center National Sex Offender Registry or on a state's sex offender registry."
      ]
    },
    {
      heading: "Prior Certifications",
      text: "I swear/affirm that, if providing certifications that have been obtained within the preceding 60 months, I have not been disqualified from employment as outlined herein or have not been convicted of an offense similar in nature to a crime listed herein under the laws or former laws of the United States or one of its territories or possessions, another state, the District of Columbia, the Commonwealth of Puerto Rico or a foreign nation, or under a former law of this State."
    },
    {
      heading: "Immediate Dismissal",
      text: "I understand that I must immediately be dismissed from employment if my certifications reveal that I am disqualified from employment."
    },
    {
      heading: "Reporting Obligation — 72 Hours",
      text: "I understand that, if I am arrested for or convicted of an offense listed herein, or am named as perpetrator in a founded or indicated report of child abuse, I will provide the administrator or designee with written notice not later than 72 hours after my arrest, conviction or receiving notification that I have been listed as a perpetrator in a founded or indicated report of child abuse in the Statewide database. I understand that, if the person responsible for employment decisions has a reasonable belief that I was arrested for or convicted of an offense listed above or was named as perpetrator in a founded or indicated report of child abuse, the person responsible will immediately require me to submit current certifications and the cost of certifications will be borne by the employing entity."
    },
    {
      heading: "Employer Discretion",
      text: "I understand that nothing in the Child Protective Services Law [State Law Reference] shall be interpreted to otherwise interfere with the ability of the employer or other person responsible for a program, activity or service from making employment, discipline or termination decisions or from establishing additional standards as part of the hiring process for employees."
    },
    {
      heading: "Record Keeping",
      text: "I understand that the employer, administrator, supervisor or other person responsible for employment decisions is required to maintain a copy of my certifications."
    }
  ],
  acknowledgementText: "I affirm that the facts set forth herein are true and correct to the best of my knowledge, information and belief. I understand that the statements herein are made subject to the penalties [State Law Reference] (relating to unsworn falsification to authorities)."
};

export const POLICY_TEMPLATES = { shaken_baby: SHAKEN_BABY, emergency_procedures: EMERGENCY_PROCEDURES, disclosure_statement: DISCLOSURE_STATEMENT };

// ── Signature Canvas ──────────────────────────────────────────────────────────
function SignatureCanvas({ canvasRef }) {
  const drawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const stop = (e) => { e.preventDefault(); drawing.current = false; };

  return (
    <canvas
      ref={canvasRef}
      width={500} height={120}
      onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
      onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
      style={{
        width: "100%", height: "120px",
        border: "1.5px solid #cbd5e1", borderRadius: "8px",
        cursor: "crosshair", background: "#fafafa", display: "block", touchAction: "none"
      }}
    />
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function PolicyTemplateViewer({ templateKey, orgName, staffName, onAcknowledge, isSubmitting, alreadyDone, stateCode = "" }) {
  const template = POLICY_TEMPLATES[templateKey];
  const canvasRef = useRef(null);
  const [signMode, setSignMode] = useState("draw");
  const [typedName, setTypedName] = useState(staffName || "");
  const [signedDate, setSignedDate] = useState(new Date().toISOString().slice(0, 10));
  const [canvasEmpty, setCanvasEmpty] = useState(true);

  // Replace [State Law Reference] with saved code everywhere
  const rc = (text) => {
    if (!text) return text;
    return text.replace(/\[State Law Reference\]/g, stateCode?.trim() || "[State Law Reference]");
  };

  useEffect(() => { if (staffName) setTypedName(staffName); }, [staffName]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setCanvasEmpty(true);
  };

  const isCanvasBlank = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext("2d");
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return !data.some(v => v !== 0);
  };

  const handleDownload = () => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 18;
    const contentW = pageW - margin * 2;
    let y = 16;

    const addLine = (text, size, style, color, maxW) => {
      pdf.setFontSize(size);
      pdf.setFont("helvetica", style || "normal");
      pdf.setTextColor(color || "#1a1a2e");
      const lines = pdf.splitTextToSize(text, maxW || contentW);
      lines.forEach(line => {
        if (y > 255) { pdf.addPage(); y = 16; }
        pdf.text(line, margin, y);
        y += size * 0.45 + 1;
      });
    };

    // Header
    pdf.setFillColor("#1a365d");
    pdf.rect(0, 0, pageW, 22, "F");
    pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor("#ffffff");
    pdf.text(orgName || "Childcare Center", pageW / 2, 9, { align: "center" });
    pdf.setFontSize(8); pdf.setFont("helvetica", "normal");
    pdf.text("Policy Acknowledgement", pageW / 2, 16, { align: "center" });
    y = 28;

    addLine(template.title, 12, "bold", "#1a365d");
    y += 3;
    if (template.intro) { addLine(rc(template.intro), 8, "italic", "#4a5568"); y += 2; }

    template.sections.forEach(sec => {
      y += 2;
      addLine(sec.heading, 9, "bold", "#1a365d");
      if (sec.text) { addLine(rc(sec.text), 8, "normal", "#374151"); }
      if (sec.items) {
        sec.items.forEach(item => {
          pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor("#374151");
          const lines = pdf.splitTextToSize(`• ${item}`, contentW - 4);
          lines.forEach((line, i) => {
            if (y > 255) { pdf.addPage(); y = 16; }
            pdf.text(line, margin + (i === 0 ? 0 : 3), y);
            y += 4.2;
          });
        });
      }
    });

    // Signature section
    y += 6;
    if (y > 230) { pdf.addPage(); y = 16; }
    pdf.setDrawColor("#94a3b8");
    pdf.line(margin, y, pageW - margin, y);
    y += 6;
    addLine("Staff Acknowledgement", 10, "bold", "#1a365d");
    y += 2;
    addLine(rc(template.acknowledgementText), 8, "italic", "#374151");
    y += 6;

    // Signature image or typed name
    if (signMode === "draw" && canvasRef.current && !isCanvasBlank()) {
      const imgData = canvasRef.current.toDataURL("image/png");
      if (y + 22 > 260) { pdf.addPage(); y = 16; }
      pdf.addImage(imgData, "PNG", margin, y, 70, 18);
      y += 22;
    } else if (signMode === "type" && typedName) {
      pdf.setFontSize(16); pdf.setFont("times", "italic"); pdf.setTextColor("#1a1a2e");
      if (y > 255) { pdf.addPage(); y = 16; }
      pdf.text(typedName, margin, y + 12);
      y += 20;
    }
    pdf.setDrawColor("#94a3b8"); pdf.line(margin, y, margin + 70, y);
    y += 4;
    addLine("Signature", 7, "normal", "#94a3b8", 70);
    y -= 4;
    const dateText = signedDate ? new Date(signedDate + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "___________";
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor("#1a1a2e");
    pdf.text(dateText, margin + 80, y - 4);
    pdf.setDrawColor("#94a3b8"); pdf.line(margin + 80, y, margin + 80 + 60, y);
    y += 4;
    addLine("Date", 7, "normal", "#94a3b8", 60);
    if (typedName) {
      y -= 4;
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor("#1a1a2e");
      pdf.text(typedName, pageW - margin - 60, y - 4);
      pdf.setDrawColor("#94a3b8"); pdf.line(pageW - margin - 60, y, pageW - margin, y);
      y += 4;
      pdf.setFontSize(7); pdf.setFont("helvetica", "normal"); pdf.setTextColor("#94a3b8");
      pdf.text("Print Name", pageW - margin - 60, y);
    }

    const fname = (template.title.slice(0, 30).replace(/[^a-z0-9]/gi, "_")) + "_Signed.pdf";
    pdf.save(fname);
  };

  if (!template) return <div style={{ color: "#dc2626", padding: 24 }}>Template not found.</div>;

  const s = { fontFamily: "system-ui, -apple-system, sans-serif" };

  return (
    <div style={{ ...s, maxWidth: 720, margin: "0 auto", padding: "0 16px 40px" }}>
      {/* Document */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 28px", marginBottom: 24 }}>
        {orgName && (
          <div style={{ textAlign: "center", marginBottom: 12, paddingBottom: 12, borderBottom: "2px solid #1a365d" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1a365d" }}>{orgName}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Policy Acknowledgement Document</div>
          </div>
        )}
        <h2 style={{ fontSize: 15, fontWeight: 700, color: "#1a365d", marginBottom: 8 }}>{template.title}</h2>
        {template.intro && <p style={{ fontSize: 13, color: "#4a5568", fontStyle: "italic", marginBottom: 12 }}>{rc(template.intro)}</p>}
        {template.sections.map((sec, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1a365d", marginBottom: 3 }}>{sec.heading}</div>
            {sec.text && <p style={{ fontSize: 12, color: "#374151", margin: "2px 0 4px" }}>{rc(sec.text)}</p>}
            {sec.items && <ul style={{ margin: "2px 0", paddingLeft: 18 }}>{sec.items.map((item, j) => (
              <li key={j} style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{item}</li>
            ))}</ul>}
          </div>
        ))}
        <div style={{ marginTop: 16, padding: "12px 16px", background: "#f0f4ff", borderLeft: "4px solid #1a365d", borderRadius: 4 }}>
          <p style={{ fontSize: 12, fontStyle: "italic", color: "#374151", margin: 0 }}>{rc(template.acknowledgementText)}</p>
        </div>
      </div>

      {/* Signature */}
      {!alreadyDone && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a365d", marginBottom: 4 }}>Sign to Acknowledge</h3>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Sign below, then download your signed copy and upload it to your Staff Records.</p>

          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {["draw", "type"].map(m => (
              <button key={m} onClick={() => setSignMode(m)} style={{
                padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1.5px solid",
                background: signMode === m ? "#1a365d" : "#fff",
                color: signMode === m ? "#fff" : "#1a365d",
                borderColor: "#1a365d"
              }}>{m === "draw" ? "Draw Signature" : "Type Instead"}</button>
            ))}
          </div>

          {signMode === "draw" ? (
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>Draw your signature below (use mouse or finger on touchscreen):</div>
              <SignatureCanvas canvasRef={canvasRef} />
              <button onClick={clearCanvas} style={{ marginTop: 6, fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Clear</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>Type your full name — it will appear in italic on the signed PDF:</div>
              <input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="Full name"
                style={{ width: "100%", padding: "8px 12px", border: "1.5px solid #cbd5e1", borderRadius: 8, fontSize: 16, fontFamily: "Georgia, serif", fontStyle: "italic", boxSizing: "border-box" }} />
            </div>
          )}

          {/* Print name + date */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Print Name</div>
              <input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="Full name"
                style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #cbd5e1", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Date</div>
              <input type="date" value={signedDate} onChange={e => setSignedDate(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #cbd5e1", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            <button onClick={handleDownload} style={{
              flex: 1, minWidth: 160, padding: "11px 0", background: "#0f766e", color: "#fff", border: "none",
              borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer"
            }}>Download Signed PDF</button>
            <button onClick={onAcknowledge} disabled={isSubmitting} style={{
              flex: 1, minWidth: 160, padding: "11px 0", background: "#1a365d", color: "#fff", border: "none",
              borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1
            }}>{isSubmitting ? "Submitting..." : "Submit Acknowledgement"}</button>
          </div>
          <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10, textAlign: "center" }}>
            Download your signed copy first, then click Submit to record your acknowledgement with your director.
          </p>
        </div>
      )}
    </div>
  );
}
